// /components/VideoEmbed/YouTubeEmbed.js
import React from 'react';

/**
 * getYouTubeVideoId
 * -------------------
 * Extracts the YouTube video ID from a URL.
 * First, it checks for a YouTube Shorts URL (which contains "/shorts/"),
 * then falls back to the regular YouTube URL patterns.
 *
 * For example:
 *   - Shorts: "https://youtube.com/shorts/NmQJYlssdyM?si=OrNI9GpfP-F51wMo" returns "NmQJYlssdyM"
 *   - Standard: "https://www.youtube.com/watch?v=VIDEO_ID" returns "VIDEO_ID"
 */
function getYouTubeVideoId(url) {
  if (!url) return null;
  
  // Check for YouTube Shorts URL.
  const shortsRegex = /youtube\.com\/shorts\/([^?\s]+)/;
  const shortsMatch = url.match(shortsRegex);
  if (shortsMatch && shortsMatch[1]) {
    return shortsMatch[1];
  }
  
  // Regular YouTube URL extraction.
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

/**
 * YouTubeEmbed Component
 * ------------------------
 * Renders a YouTube video embed in an iframe.
 * It extracts the video ID from the URL and builds the embed URL.
 * The iframe's onLoad event dispatches a resize event so parent components can adjust.
 */
const YouTubeEmbed = ({ url }) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return <p>Invalid YouTube URL</p>;
  return (
    <iframe
      width="560"
      height="315"
      src={`https://www.youtube.com/embed/${videoId}`}
      frameBorder="0"
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      title="YouTube video player"
      onLoad={() => window.dispatchEvent(new Event('resize'))}
    />
  );
};

export default YouTubeEmbed;
